export type TimeDisplayMode = "local" | "utc";

/**
 * Utility class for date formatting operations
 */
export class DateFormatter {
  private static readonly MS_PER_SECOND = 1000;
  private static readonly MS_PER_MINUTE = 60000;
  private static readonly MS_PER_HOUR = 3600000;
  private static readonly MS_PER_DAY = 86400000;
  private static readonly SECONDS_PER_MINUTE = 60;

  /**
   * Format a date string to locale string, in local time or UTC
   */
  static formatDate(date?: string, fallback = "-", mode: TimeDisplayMode = "local"): string {
    if (!date) {
      return fallback;
    }
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) {
      return fallback;
    }
    if (mode === "utc") {
      return `${parsed.toLocaleString(undefined, { timeZone: "UTC" })} UTC`;
    }
    return parsed.toLocaleString();
  }

  /**
   * Format a date string as a coarse relative time ("5 min ago", "in 2 h")
   */
  static formatRelative(date?: string, fallback = "-", now: Date = new Date()): string {
    if (!date) {
      return fallback;
    }
    const timestamp = new Date(date).getTime();
    if (Number.isNaN(timestamp)) {
      return fallback;
    }

    const diffMs = now.getTime() - timestamp;
    const elapsed = Math.abs(diffMs);
    if (elapsed < this.MS_PER_MINUTE) {
      return diffMs >= 0 ? "just now" : "in under a minute";
    }

    let amount: number;
    let unit: string;
    if (elapsed < this.MS_PER_HOUR) {
      amount = Math.floor(elapsed / this.MS_PER_MINUTE);
      unit = "min";
    } else if (elapsed < this.MS_PER_DAY) {
      amount = Math.floor(elapsed / this.MS_PER_HOUR);
      unit = "h";
    } else {
      amount = Math.floor(elapsed / this.MS_PER_DAY);
      unit = "d";
    }

    return diffMs >= 0 ? `${amount} ${unit} ago` : `in ${amount} ${unit}`;
  }

  /**
   * Calculate and format duration between two dates
   */
  static formatDuration(startDate?: string, endDate?: string, runningText = "Running...", fallback = "-"): string {
    if (!endDate) {
      return runningText;
    }

    const ms = new Date(endDate).getTime() - new Date(startDate ?? "").getTime();
    if (Number.isNaN(ms) || ms < 0) {
      return fallback;
    }
    return this.formatMilliseconds(ms);
  }

  /**
   * Format milliseconds to human readable string
   */
  static formatMilliseconds(ms: number): string {
    if (ms < this.MS_PER_SECOND) {
      return `${ms}ms`;
    }
    if (ms < this.MS_PER_MINUTE) {
      return `${(ms / this.MS_PER_SECOND).toFixed(1)}s`;
    }

    const minutes = Math.floor(ms / this.MS_PER_MINUTE);
    const seconds = Math.floor((ms % this.MS_PER_MINUTE) / this.MS_PER_SECOND);
    return `${minutes}m ${seconds}s`;
  }
}
